import React from 'react'
import { format } from 'date-fns'
import { PiCalendarBlank, PiCaretDownBold, PiCaretUpBold } from 'react-icons/pi'
import {
  PerformanceRange,
  PERFORMANCE_RANGE_LABELS,
} from '../../services/recommendations_api'
import { FbDropdown } from '../FbUI/FbDropdown'
import {
  secondaryPillButtonClass,
  secondaryPillHoverClass,
} from '../FbUI/secondaryPillButton'
import { cn } from '../UI/cn'
import {
  DateRangePicker,
  type DateRangeValue,
} from '../../pages/app/deals/DateRangePicker'

/**
 * Ordered list of preset range options for the dropdown. `custom` is appended
 * separately so it always sits last, after the presets.
 */
const PRESET_OPTIONS: PerformanceRange[] = ['3m', '6m', '12m', 'ytd', 'all']

interface RangeSelectorProps {
  /** Currently selected time range. */
  value: PerformanceRange
  /** Callback fired when the user selects a different range. */
  onChange: (value: PerformanceRange) => void
  /**
   * Currently selected custom range. Only meaningful when `value === 'custom'`;
   * the parent owns this state so the dashboard can refetch on change. Optional
   * so callers that never expose the "Custom" option need not wire it up.
   */
  customRange?: DateRangeValue
  /** Callback fired when the user picks/edits the custom start/end range. */
  onCustomRangeChange?: (range: DateRangeValue) => void
  /** Optional additional CSS class names. */
  className?: string
}

/**
 * Dropdown selector for choosing a time range for performance data.
 *
 * Provides preset options (3m, 6m, 12m, YTD, All Time) plus a "Custom" option.
 * When "Custom" is active a reusable two-month calendar range picker is shown
 * beside the dropdown so the user can set/adjust an explicit start/end window;
 * picking any preset from the dropdown switches back to the preset view.
 */
export const RangeSelector: React.FC<RangeSelectorProps> = ({
  value,
  onChange,
  customRange,
  onCustomRangeChange,
  className,
}) => {
  const items = [
    ...PRESET_OPTIONS.map((range) => ({
      key: range,
      label: PERFORMANCE_RANGE_LABELS[range],
    })),
    { key: 'custom' as const, label: PERFORMANCE_RANGE_LABELS.custom },
  ]

  // Committed custom window, shown on the calendar pill to its right. The
  // dropdown itself just names the active option ("Custom"); the explicit
  // dates and the calendar affordance live on the pill that opens the picker.
  const customRangeLabel = customRange?.from
    ? customRange.to
      ? `${format(customRange.from, 'd MMM yyyy')} – ${format(customRange.to, 'd MMM yyyy')}`
      : format(customRange.from, 'd MMM yyyy')
    : 'Pick dates'

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <FbDropdown
        label={PERFORMANCE_RANGE_LABELS[value]}
        icon={<PiCalendarBlank size={14} className="flex-shrink-0" />}
        items={items}
        selectedKey={value}
        onAction={(key) => onChange(key as PerformanceRange)}
        className={secondaryPillHoverClass}
        placement="bottom end"
        size="sm"
      />
      {value === 'custom' && onCustomRangeChange && (
        <DateRangePicker
          value={customRange}
          onChange={onCustomRangeChange}
          renderTrigger={({ open }) => (
            // Reuse the shared secondary-pill shape so this control lines up
            // (border/hover) with the FbDropdown beside it, then override to the
            // compact `sm` size (min-h-8/text-sm/gap-1.5) so it matches the
            // `size="sm"` dropdown. The override stays local so other
            // secondaryPillButtonClass consumers are unaffected. Swap the border
            // to brand-active once a range is committed.
            <button
              type="button"
              className={cn(
                secondaryPillButtonClass,
                'gap-1.5 min-h-8 px-2.5 text-sm',
                customRange?.from && '!border-brand-active'
              )}
            >
              <PiCalendarBlank size={14} className="flex-shrink-0" />
              <span>{customRangeLabel}</span>
              {open ? (
                <PiCaretUpBold
                  size={14}
                  className="text-current opacity-70 flex-shrink-0"
                />
              ) : (
                <PiCaretDownBold
                  size={14}
                  className="text-current opacity-70 flex-shrink-0"
                />
              )}
            </button>
          )}
        />
      )}
    </div>
  )
}
